import type { ResponseMerchFlowDetails } from "@CommonApi/merchandise";
import { ReviewStatus } from "@CommonTypes/merchflow/ReviewStatus";
import type { MenuOption } from "src/components/Menu";

export interface MerchflowRowActionCallbacks {
  onContinue: (merchflow: ResponseMerchFlowDetails) => void;
  onViewStores: (merchflow: ResponseMerchFlowDetails) => void;
  onViewTemplates: (merchflow: ResponseMerchFlowDetails) => void;
  onUpdate: (merchflow: ResponseMerchFlowDetails) => void;
  onViolationReport: (merchflow: ResponseMerchFlowDetails) => void;
  onDuplicate: (merchflow: ResponseMerchFlowDetails) => void;
  onDelete: (merchflow: ResponseMerchFlowDetails) => void;
}

interface BuildMerchflowRowMenuOptionsInput {
  merchflow: ResponseMerchFlowDetails;
  canCreate: boolean;
  canDelete: boolean;
  hideMerchflowActions: boolean;
  callbacks: MerchflowRowActionCallbacks;
}

export interface MerchflowInlineIndicator {
  kind: "violation" | "need_update";
  tooltip: string;
  onClick: () => void;
}

const canShowUpdateMenuOption = (merchflow: ResponseMerchFlowDetails) =>
  merchflow.status === ReviewStatus.STORE_POG_GENERATED ||
  merchflow.status === ReviewStatus.STORE_POG_EXPORTED;

export const buildMerchflowRowMenuOptions = ({
  merchflow,
  canCreate,
  canDelete,
  hideMerchflowActions,
  callbacks,
}: BuildMerchflowRowMenuOptionsInput): MenuOption[] => [
  ...(!hideMerchflowActions && canCreate
    ? [{ label: "Continue", onClick: () => callbacks.onContinue(merchflow) }]
    : []),
  { label: "View Stores", onClick: () => callbacks.onViewStores(merchflow) },
  ...(!hideMerchflowActions
    ? [{ label: "View Templates", onClick: () => callbacks.onViewTemplates(merchflow) }]
    : []),
  ...(canShowUpdateMenuOption(merchflow)
    ? [{ label: "Update", onClick: () => callbacks.onUpdate(merchflow) }]
    : []),
  ...(!hideMerchflowActions && merchflow.is_violated
    ? [{ label: "Violation Report", onClick: () => callbacks.onViolationReport(merchflow) }]
    : []),
  ...(!hideMerchflowActions && canCreate
    ? [{ label: "Duplicate", onClick: () => callbacks.onDuplicate(merchflow) }]
    : []),
  ...(!hideMerchflowActions && canDelete
    ? [{ label: "Delete", onClick: () => callbacks.onDelete(merchflow) }]
    : []),
];

export const buildMerchflowInlineIndicators = ({
  merchflow,
  hideMerchflowActions,
  callbacks,
}: Pick<
  BuildMerchflowRowMenuOptionsInput,
  "merchflow" | "callbacks" | "hideMerchflowActions"
>): MerchflowInlineIndicator[] => [
  ...(!hideMerchflowActions && merchflow.is_violated
    ? [
        {
          kind: "violation" as const,
          tooltip: "Planogram violation detected",
          onClick: () => callbacks.onViolationReport(merchflow),
        },
      ]
    : []),
  ...(merchflow.need_update
    ? [
        {
          kind: "need_update" as const,
          tooltip: "Planogram update needed",
          onClick: () => callbacks.onUpdate(merchflow),
        },
      ]
    : []),
];
